/* eslint-disable prefer-regex-literals */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, BasicInput, TertiaryButton, Typography } from '@dev/ee-components';
import { SetCheckoutErrors, SetUrlPayload, getCheckoutCreatorStore } from '@/store/checkoutCreator/actions';
import { Box, Grid, Link } from '@material-ui/core';

import { LANDING_PAGE_DOMAIN, MAX_SUFFIX_LENGTH } from '@/services/constants';

import { useDialog } from '@/services/Dialog/useDialog';
import { DialogIds } from '@/services/Dialog/DialogIds.enum';
import { SetLandingPageErrors } from '@/store/landingPageCreator/actions';
import { CheckoutErrorNames } from '@/store/checkoutCreator/types';
import { Step3EditContentInputWrapper, Step3EditContentWrapper, StyledCounter } from './styles';
import { useLandingPageSubdomain } from '../../hooks/useLandingPageSubdomain';

const DEFAULT_LANDING_SUBDOMAIN = 'subdomain'
export const validationRegEx = new RegExp(/^[a-zA-Z0-9-]+$/);


export const Step3EditContent = (): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { errors, checkoutObject } = useSelector(getCheckoutCreatorStore);
    const {accountSubdomain,landingPageSubdomain,checkoutBaseUrl,loading} = useLandingPageSubdomain();
    const [inputValue, setInputValue] = useState<string>(null);
    const {showDialog} = useDialog();

    const currentSubdomain = accountSubdomain || DEFAULT_LANDING_SUBDOMAIN;

    useEffect(()=>{
        if(loading || inputValue !== null || (landingPageSubdomain && (landingPageSubdomain?.LandingPageID !== checkoutObject?.ProductLanding?.LandingPageID))){
            return;
        }
        setInputValue((landingPageSubdomain?.UrlSuffix?.startsWith?.('/') ? landingPageSubdomain?.UrlSuffix?.substring(1): landingPageSubdomain?.UrlSuffix) || "");
    },[loading,inputValue,landingPageSubdomain])

    useEffect(()=>{
        const composedLandingPageUrl = `${checkoutBaseUrl}/${inputValue}`
        
        dispatch(SetUrlPayload(composedLandingPageUrl));
        
        if (errors) {
            dispatch(SetLandingPageErrors());
        }
        return ()=>{
           dispatch(SetCheckoutErrors({
                [CheckoutErrorNames.INVALID_SUFFIX]: false
            }))
        }
    },[inputValue,accountSubdomain])
   
    const availableErrors = [
        {
            error: !!errors?.landing_subdirectory_in_use,
            msg: t('landing_subdirectory_in_use')
        },
        {
            error: !!errors?.landing_invalid_suffix,
            msg: t('landing_invalid_suffix')
        },
    ].find(e=>e.error);

    const lackOfAccountSubdomain = !!errors?.landing_lack_of_account_subdomain;

    const validateAndChange = (e) => {
        const newValue = e?.target?.value;
        if (newValue?.length > MAX_SUFFIX_LENGTH || !isValidInput(newValue)) return;
        setInputValue(newValue);
    };

    const isValidInput = (value = inputValue) => {
        if (!validationRegEx.test(value) && !!value) return false;

        //remove error if existed
        errors?.landing_invalid_suffix && dispatch(SetCheckoutErrors({
            [CheckoutErrorNames.INVALID_SUFFIX]: false,
            [CheckoutErrorNames.SUBDOMAIN_LANDING_SUBDIRECTORY_IN_USE]: false,
        }))
        
        return true;
    };

    const openAddSubdomainDialog = () => {
        showDialog(DialogIds.AddLandingPageSubdomain)
    }
    
    return (
        <Step3EditContentWrapper>
            <Step3EditContentInputWrapper>
                <Grid container alignItems="baseline">
                    <Grid item style={{ height: 44, marginRight: 4 }}>
                        <Typography variant="body1" bodyVariant="onPaper">{`${currentSubdomain}.${LANDING_PAGE_DOMAIN}/ `}</Typography>
                    </Grid>
                    <Grid item style={{ flex: 1 }}>
                        <BasicInput
                            fullWidth
                            onInput={validateAndChange}
                            value={inputValue ?? ''}
                            error={!!availableErrors?.error}
                            errorMessage={availableErrors?.msg}
                            inputProps={{
                                maxLength:MAX_SUFFIX_LENGTH
                            }}
                            disabled={inputValue===null}
                        />
                        {!availableErrors?.msg && (
                            <StyledCounter container justifyContent="flex-end">
                                <Typography variant="body1" bodyVariant="help">{`${
                                    inputValue?.length || 0
                                }/${MAX_SUFFIX_LENGTH}`}</Typography>
                            </StyledCounter>
                        )}
                    </Grid> 
                </Grid>
            </Step3EditContentInputWrapper>
            
            {!accountSubdomain && (<>
            {lackOfAccountSubdomain && <Box mt="32px"><Alert severity='error' body={<Trans i18nKey="step3_no_subdomain_website_error" components={{l: <Link onClick={openAddSubdomainDialog} />}} />} /></Box>}
            
            <Grid style={{marginTop:32}} container justifyContent="space-between"><Typography variant="body1">{t('step3_addsubdomain_desc')}</Typography>
                <TertiaryButton id="step3-add-subdomain" text={t('addsubdomain')} onClick={openAddSubdomainDialog} />
            </Grid></>)}
        </Step3EditContentWrapper>
    );
};
